import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { Subject } from 'rxjs'
import { flatMap, take, takeUntil } from 'rxjs/operators'

import { KeyNodeType } from '@overtur-monorepo/keying-shared'
import { MasterKeyService } from '../../services/keying.masterkey.service'
import { KeyingStructureService } from '../../services/keying.structure.service'
import { KeyingSystemService } from '../../services/keying.system.service'
import { ErrorService } from '../../services/error.service'
import { KeySystem, Structure } from '../../models/dev.test.models'

@Component({
  selector: 'app-cross-key-range-dialog',
  templateUrl: './cross-key-range-dialog.component.html',
  styleUrls: ['./cross-key-range-dialog.component.scss'],
  standalone: false
})
export class CrossKeyRangeDialogComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  keyNodeType = KeyNodeType
  crossKeyRangeForm: FormGroup

  selectiveData: any
  lastCrossKey
  structure: Structure
  keySystem: KeySystem

  skipErrors = false

  focus = {
    prefix: false,
    from: false,
    to: false,
    suffix: false,
  }

  public errorMessages = {
    prefix: [
      { type: 'required', message: 'Prefix is required.' },
      { type: 'beginsWithX', message: 'Must start with X.' },
      { type: 'invalidAlphanumeric', message: 'Only letters and numbers are allowed.' },
    ],
    from: [
      { type: 'required', message: 'From is required.' },
      { type: 'invalidFormat', message: 'Must be numbers or a single letter (no special characters or spaces).' },
      { type: 'fromMustBeLessOrEqual', message: 'From must be equal to or less than To.' },
      { type: 'typeMismatch', message: 'From and To must both be numbers or both be letters.' }
    ],
    to: [
      { type: 'required', message: 'To is required.' },
      { type: 'invalidFormat', message: 'Must be numbers or a single letter (no special characters or spaces).' },
      { type: 'toMustBeGreaterOrEqual', message: 'To must be equal to or greater than From.' },
      { type: 'typeMismatch', message: 'From and To must both be numbers or both be letters.' }
    ],
    suffix: [
      { type: 'invalidAlphanumeric', message: 'Only letters and numbers are allowed.' },
    ],
  }

  prefix = 'X'

  constructor(
    public form: FormBuilder,
    private readonly dialogRef: MatDialogRef<CrossKeyRangeDialogComponent>,
    private readonly masterKeyService: MasterKeyService,
    private readonly keyingStructureService: KeyingStructureService,
    private readonly keyingSystemService: KeyingSystemService,
    private readonly errorService: ErrorService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.selectiveData = data

    if (this.validate()) {
      this.createForm()
    }
  }

  validate() {
    if (!this.selectiveData) return false
    if (!this.selectiveData.action) return false
    return true
  }

  /* istanbul ignore next */
  createForm(): void {
    this.crossKeyRangeForm = this.form.group({
      prefix: new FormControl(this.prefix, [Validators.required, this.beginsWithXValidator, this.alphanumericValidator]),
      from: new FormControl('', [Validators.required, this.fromToValidator]),
      to: new FormControl('', [Validators.required, this.fromToValidator]),
      suffix: new FormControl('', [this.alphanumericValidator]),
    })

    // Add cross-field validators for from/to comparison
    this.crossKeyRangeForm.get('from').setValidators([
      Validators.required,
      this.fromToValidator,
      this.fromLessThanOrEqualValidator.bind(this),
      this.fromTypeMismatchValidator.bind(this)
    ])

    this.crossKeyRangeForm.get('to').setValidators([
      Validators.required,
      this.fromToValidator,
      this.toGreaterThanOrEqualValidator.bind(this),
      this.toTypeMismatchValidator.bind(this)
    ])
  }

  /* istanbul ignore next */
  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  /* istanbul ignore next */
  ngOnInit() {
    this.keyingStructureService.structure$
      .pipe(
        takeUntil(this.destroy$),
        flatMap((structure: Structure) => {
          this.structure = structure
          return this.keyingSystemService.keySystem$
        })
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (keySystem: KeySystem) => {
          this.keySystem = keySystem
          this.getLastCrossKey()
        },
        (error) => {
          this.errorService.throw(error)
        }
      )
  }

  /* istanbul ignore next */
  getLastCrossKey() {
    this.masterKeyService
      .getLastCrossKey(this.selectiveData.structure.id, this.selectiveData.keySystem.id)
      .pipe(take(1))
      .subscribe((lastCrossKey) => {
        this.lastCrossKey = lastCrossKey
      })
  }

  /* istanbul ignore next */
  private collectAllKeysRecursively(node: any, keysArray: any[] = []): any[] {
    // Skip nodes with masterKeyTypeId === 12 and all their children
    if (node.masterKeyTypeId === 12) {
      return keysArray
    }

    // Add current node to the array
    keysArray.push(node)

    // Recursively process all children
    if (node.children && Array.isArray(node.children)) {
      for (const child of node.children) {
        this.collectAllKeysRecursively(child, keysArray)
      }
    }

    return keysArray
  }

  /* istanbul ignore next */
  validateCylinderRange(): { valid: boolean; allExist: boolean } {
    if (!this.keySystem) {
      return { valid: true, allExist: false } // Cannot validate yet, allow submission
    }

    const prefix = this.crossKeyRangeForm.controls.prefix.value?.toUpperCase() || ''
    const fromValue = this.crossKeyRangeForm.controls.from.value
    const toValue = this.crossKeyRangeForm.controls.to.value
    const suffix = this.crossKeyRangeForm.controls.suffix.value?.toUpperCase() || ''

    // Generate all cylinder names in the range
    const cylinderNames = this.generateRangeCylinderNames(prefix, fromValue, toValue, suffix)

    // Build our own collection by traversing the tree directly
    const allKeys = this.collectAllKeysRecursively(this.keySystem.masterKeysKeying)

    // Filter to get only non-future keys
    const keys = allKeys.filter((k) => (k.isFutureKey === false))
    // Get all existing cross keyed cylinders
    const crossKeyMaster = this.keySystem.masterKeysKeying.children.find(
      (k) => k.masterKeyTypeId === this.keyNodeType.CrossKeyMaster
    )
    const existingCrossKeys = crossKeyMaster?.children || []

    // Check each cylinder in the range
    let atLeastOneValid = false
    let allCylindersExist = true
    let checkedCount = 0

    for (const cylinderName of cylinderNames) {
      // Skip if doesn't start with X
      if (!cylinderName.startsWith('X')) {
        continue
      }

      checkedCount++

      // Check if cylinder already exists
      const cylinderExists = existingCrossKeys.some(
        (x) => x.keySetSymbol.toUpperCase() === cylinderName.toUpperCase()
      )

      if (!cylinderExists) {
        allCylindersExist = false
      }

      const lastChar = cylinderName.charAt(cylinderName.length - 1)

      // Case 1: Cylinder starts with X but does not end with X
      if (lastChar !== 'X') {
        const keyName = cylinderName.replace(/X/g, '')
        const nameFound = keys.some((x) =>
          x.keySetSymbol?.trim().toLowerCase() === keyName.trim().toLowerCase()
        )
        if (nameFound) {
          atLeastOneValid = true
        }
      }
      // Case 2: Cylinder starts with X and ends with X
      else {
        const keyName = cylinderName.replace(/X/g, '')
        const isOnlyNumbers = /^\d+$/.test(keyName)

        // Case 3: Middle part is only numbers - no check needed
        if (isOnlyNumbers) {
          atLeastOneValid = true
        }
        // Case 2: Middle part contains letters - check if at least one exists
        else {
          const nameFound = keys.some((x) => x.keySetSymbol?.trim().toLowerCase() === keyName.trim().toLowerCase())
          if (nameFound) {
            atLeastOneValid = true
          }
        }
      }
    }

    // If all cylinders exist in the system
    if (checkedCount > 0 && allCylindersExist) {
      return { valid: false, allExist: true }
    }

    return { valid: atLeastOneValid, allExist: false }
  }

  /* istanbul ignore next */
  generateRangeCylinderNames(prefix: string, fromValue: string, toValue: string, suffix: string): string[] {
    const cylinderNames: string[] = []

    const isNumericRange = /^\d+$/.test(fromValue)

    if (isNumericRange) {
      // Numeric range
      const fromNum = parseInt(fromValue, 10)
      const toNum = parseInt(toValue, 10)

      for (let i = fromNum; i <= toNum; i++) {
        cylinderNames.push(`${prefix}${i}${suffix}`)
      }
    } else {
      // Letter range
      const fromCharCode = fromValue.charCodeAt(0)
      const toCharCode = toValue.charCodeAt(0)

      for (let i = fromCharCode; i <= toCharCode; i++) {
        cylinderNames.push(`${prefix}${String.fromCharCode(i)}${suffix}`)
      }
    }

    return cylinderNames
  }

  /* istanbul ignore next */
  beginsWithXValidator = (control: FormControl) => {
    let value = control.value
    if (value !== '') {
      const beginsWithX = value.charAt(0).toUpperCase() === 'X'
      if (!beginsWithX) {
        return { beginsWithX: true, type: 'beginsWithX' }
      }
    }
    return null
  }

  /* istanbul ignore next */
  alphanumericValidator = (control: FormControl) => {
    const value = control.value
    // Allow empty values (for optional fields like suffix)
    if (value && value !== '') {
      // Only allow letters and numbers (no special characters or spaces)
      const isValid = /^[A-Z0-9]+$/i.exec(value)
      if (!isValid) {
        return { invalidAlphanumeric: true, type: 'invalidAlphanumeric' }
      }
    }
    return null
  }

  /* istanbul ignore next */
  fromToValidator = (control: FormControl) => {
    let value = control.value
    if (value !== '') {
      // Must be either all digits or a single letter (A-Z)
      // No special characters or spaces allowed
      const isValid = /^(\d+|[A-Z])$/.exec(value)
      if (!isValid) {
        return { invalidFormat: true, type: 'invalidFormat' }
      }
    }
    return null
  }

  /* istanbul ignore next */
  fromLessThanOrEqualValidator = (control: FormControl) => {
    if (!this.crossKeyRangeForm) {
      return null
    }

    const fromValue = control.value
    const toValue = this.crossKeyRangeForm.get('to')?.value

    // Only validate if both values are present
    if (fromValue && toValue) {
      const fromIsNumber = /^\d+$/.exec(fromValue)
      const toIsNumber = /^\d+$/.exec(toValue)
      const fromIsLetter = /^[A-Z]$/.exec(fromValue)
      const toIsLetter = /^[A-Z]$/.exec(toValue)

      // Compare numbers
      if (fromIsNumber && toIsNumber) {
        const fromNum = parseInt(fromValue, 10)
        const toNum = parseInt(toValue, 10)

        if (fromNum > toNum) {
          return { fromMustBeLessOrEqual: true, type: 'fromMustBeLessOrEqual' }
        }
      }

      // Compare letters alphabetically
      if (fromIsLetter && toIsLetter) {
        if (fromValue > toValue) {
          return { fromMustBeLessOrEqual: true, type: 'fromMustBeLessOrEqual' }
        }
      }
    }

    return null
  }

  /* istanbul ignore next */
  toGreaterThanOrEqualValidator = (control: FormControl) => {
    if (!this.crossKeyRangeForm) {
      return null
    }

    const fromValue = this.crossKeyRangeForm.get('from')?.value
    const toValue = control.value

    // Only validate if both values are present
    if (fromValue && toValue) {
      const fromIsNumber = /^\d+$/.exec(fromValue)
      const toIsNumber = /^\d+$/.exec(toValue)
      const fromIsLetter = /^[A-Z]$/.exec(fromValue)
      const toIsLetter = /^[A-Z]$/.exec(toValue)

      // Compare numbers
      if (fromIsNumber && toIsNumber) {
        const fromNum = parseInt(fromValue, 10)
        const toNum = parseInt(toValue, 10)

        if (toNum < fromNum) {
          return { toMustBeGreaterOrEqual: true, type: 'toMustBeGreaterOrEqual' }
        }
      }

      // Compare letters alphabetically
      if (fromIsLetter && toIsLetter) {
        if (toValue < fromValue) {
          return { toMustBeGreaterOrEqual: true, type: 'toMustBeGreaterOrEqual' }
        }
      }
    }

    return null
  }

  /* istanbul ignore next */
  fromTypeMismatchValidator = (control: FormControl) => {
    if (!this.crossKeyRangeForm) {
      return null
    }

    const fromValue = control.value
    const toValue = this.crossKeyRangeForm.get('to')?.value

    // Only validate if both values are present
    if (fromValue && toValue) {
      const fromIsNumber = /^\d+$/.exec(fromValue)
      const fromIsLetter = /^[A-Z]$/.exec(fromValue)
      const toIsNumber = /^\d+$/.exec(toValue)
      const toIsLetter = /^[A-Z]$/.exec(toValue)

      // If from is a number, to must be a number
      // If from is a letter, to must be a letter
      if ((fromIsNumber && !toIsNumber) || (fromIsLetter && !toIsLetter)) {
        return { typeMismatch: true, type: 'typeMismatch' }
      }
    }

    return null
  }

  /* istanbul ignore next */
  toTypeMismatchValidator = (control: FormControl) => {
    if (!this.crossKeyRangeForm) {
      return null
    }

    const fromValue = this.crossKeyRangeForm.get('from')?.value
    const toValue = control.value

    // Only validate if both values are present
    if (fromValue && toValue) {
      const fromIsNumber = /^\d+$/.exec(fromValue)
      const fromIsLetter = /^[A-Z]$/.exec(fromValue)
      const toIsNumber = /^\d+$/.exec(toValue)
      const toIsLetter = /^[A-Z]$/.exec(toValue)

      // If from is a number, to must be a number
      // If from is a letter, to must be a letter
      if ((fromIsNumber && !toIsNumber) || (fromIsLetter && !toIsLetter)) {
        return { typeMismatch: true, type: 'typeMismatch' }
      }
    }

    return null
  }

  /* istanbul ignore next */
  onFromToInput(event: any, fieldName: string) {
    let value = event.target.value
    const currentValue = this.crossKeyRangeForm.controls[fieldName].value || ''

    // Convert to uppercase and remove special characters
    value = value.toUpperCase().replace(/[^A-Z0-9]/g, '')

    if (value.length > 0) {
      value = this.sanitizeFromToValue(value, currentValue)
    }

    // Update the form control value
    this.crossKeyRangeForm.controls[fieldName].setValue(value)

    // Re-validate both fields when either changes
    this.revalidateOppositeField(fieldName)
  }

  /* istanbul ignore next */
  private sanitizeFromToValue(value: string, currentValue: string): string {
    const currentIsLetter = /^[A-Z]$/.exec(currentValue)
    const currentIsNumber = /^\d+$/.exec(currentValue)
    const hasLetter = /[A-Z]/.exec(value)
    const hasNumber = /\d/.exec(value)

    // Block numbers if we already have a letter
    if (currentIsLetter && hasNumber) {
      return currentValue
    }

    // Block letters if we already have numbers
    if (currentIsNumber && hasLetter) {
      return currentValue
    }

    // If it's a new entry and contains both, decide based on first character
    if (hasLetter && hasNumber) {
      return this.extractSingleType(value)
    }

    // If it contains only letters, allow only a single letter
    if (hasLetter) {
      return this.extractSingleLetter(value)
    }

    return value
  }

  /* istanbul ignore next */
  private extractSingleType(value: string): string {
    if (/^[A-Z]/.exec(value)) {
      return this.extractSingleLetter(value)
    }
    return value.replace(/[A-Z]/g, '')
  }

  /* istanbul ignore next */
  private extractSingleLetter(value: string): string {
    const match = value.match(/[A-Z]/)
    return match ? match[0] : ''
  }

  /* istanbul ignore next */
  private revalidateOppositeField(fieldName: string): void {
    const oppositeField = fieldName === 'from' ? 'to' : 'from'
    this.crossKeyRangeForm.controls[oppositeField]?.updateValueAndValidity()
  }

  /* istanbul ignore next */
  onAlphanumericInput(event: any, fieldName: string) {
    let value = event.target.value

    // Convert to uppercase
    value = value.toUpperCase()

    // Remove anything that is not a letter or number
    value = value.replace(/[^A-Z0-9]/g, '')

    // Update the form control value
    this.crossKeyRangeForm.controls[fieldName].setValue(value)
  }

  /* istanbul ignore next */
  displayName(subject) {
    if (subject.name) {
      return subject?.name || subject
    } else if (subject.description) {
      return subject?.description || subject
    }
  }

  /* istanbul ignore next */
  submit() {
    if (this.crossKeyRangeForm.valid) {
      // Validate cylinder range
      const validationResult = this.validateCylinderRange()

      // Check if all cylinders already exist
      if (validationResult.allExist) {
        this.errorService.showError('All requested cylinders already exist.')
        return
      }

      // Check if at least one deafult key symbol exists
      if (!validationResult.valid) {
        this.errorService.showError('At least one default key symbol must exist in the system.')
        return
      }

      const data = {
        masterKey: this.selectiveData.masterKey,
        action: this.selectiveData.action,
        prefix: this.crossKeyRangeForm.controls.prefix.value,
        from: this.crossKeyRangeForm.controls.from.value,
        to: this.crossKeyRangeForm.controls.to.value,
        suffix: this.crossKeyRangeForm.controls.suffix.value,
      }

      this.dialogRef.close(data)
    }
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.invalid && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  close() {
    this.dialogRef.close()
  }
}
